import React, { useState } from "react";
import { fmtDate } from "@/App";
import { Calendar, User } from "lucide-react";

export function KanbanBoard({ columns, onDropTask, renderCard, projects }) {
  const [selectedTask, setSelectedTask] = useState(null);

  const renderTaskDetails = (task) => {
    const excludedKeys = ["id", "project_id", "assignee_id"];

    return (
      <div className="space-y-4">
        {Object.entries(task).map(([key, value]) => {
          if (excludedKeys.includes(key)) return null;

          if (key === "due_date") {
            return (
              <div
                key={key}
                className="flex items-start border-b border-gray-100 pb-3"
              >
                <span className="text-xs uppercase tracking-wider text-gray-500 font-medium w-32 flex-shrink-0 pt-0.5">
                  Due Date
                </span>
                <span className="text-sm text-gray-900 font-medium">
                  {fmtDate(value)}
                </span>
              </div>
            );
          }

          if (key === "createdAt") {
            return (
              <div
                key={key}
                className="flex items-start border-b border-gray-100 pb-3"
              >
                <span className="text-xs uppercase tracking-wider text-gray-500 font-medium w-32 flex-shrink-0 pt-0.5">
                  Created
                </span>
                <span className="text-sm text-gray-900">{fmtDate(value)}</span>
              </div>
            );
          }

          if (Array.isArray(value)) {
            return (
              <div
                key={key}
                className="flex items-start border-b border-gray-100 pb-3"
              >
                <span className="text-xs uppercase tracking-wider text-gray-500 font-medium w-32 flex-shrink-0 pt-0.5">
                  {key.replaceAll("_", " ")}
                </span>
                <span className="text-sm text-gray-900">
                  {value.length > 0 ? (
                    value.join(", ")
                  ) : (
                    <span className="text-gray-400 italic">None</span>
                  )}
                </span>
              </div>
            );
          }

          return (
            <div
              key={key}
              className="flex items-start border-b border-gray-100 pb-3"
            >
              <span className="text-xs uppercase tracking-wider text-gray-500 font-medium w-32 flex-shrink-0 pt-0.5">
                {key.replaceAll("_", " ")}
              </span>
              <span className="text-sm text-gray-900">{String(value)}</span>
            </div>
          );
        })}

        {/* Assignee name only */}
        {task.assignee_id?.name && (
          <div className="flex items-start border-b border-gray-100 pb-3">
            <span className="text-xs uppercase tracking-wider text-gray-500 font-medium w-32 flex-shrink-0 pt-0.5">
              Assignee
            </span>
            <span className="text-sm text-gray-900 font-medium">
              {task.assignee_id.name}
            </span>
          </div>
        )}
      </div>
    );
  };
  return (
    <div className="kanban">
      {columns.map((col) => (
        <div
          key={col.key}
          className="kanban-col"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            const tid = e.dataTransfer.getData("text/task-id");
            if (tid) onDropTask(tid, col.key);
          }}
        >
          <div className="kanban-col-head">
            {col.title} <span className="count">{col.items.length}</span>
          </div>
          <div className="kanban-col-body">
            {col.items.map((it) => (
              <div
                key={it.id}
                className={`kanban-card p-${(
                  it.priority || "Medium"
                ).toLowerCase()}`}
                draggable
                onDragStart={(e) =>
                  e.dataTransfer.setData("text/task-id", it.id)
                }
                onClick={() => setSelectedTask(it)}
              >
                {renderCard ? (
                  renderCard(it)
                ) : (
                  <div>
                    <div className=" text-blue-700 text-center px-2 py-1 rounded">
                      {projects.find((p) => p._id === it.project_id)?.name} - {it.stage_id}
                    </div>
                    <div className="card-title">{it.title}</div>
                    <div className="card-meta flex items-center gap-2">
                      <Calendar size={16} />
                      <span>
                        {fmtDate(it.due_date)} • {it.priority}
                      </span>
                    </div>
                    <div className="card-meta flex items-center gap-2">
                      <User size={16} />
                      <span>{it.assignee_id?.name || "Unassigned"}</span>
                    </div>
                    <div className="card-meta flex items-center justify-end text-sm text-gray-500 gap-2">
                      <Calendar size={14} className="text-gray-500" />
                      <span>{fmtDate(it.due_date)}</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {col.items.length === 0 && (
              <div className="kanban-empty">No tasks</div>
            )}
          </div>
        </div>
      ))}

      {/* Improved Dialog */}
      {selectedTask && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-auto relative">
            {/* Close button at top-right */}
            <button
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 text-lg font-bold"
              onClick={() => setSelectedTask(null)}
            >
              &times;
            </button>

            <div className="p-6">
              <h2 className="text-2xl font-semibold mb-4 text-center">
                {selectedTask.title}
              </h2>
              {renderTaskDetails(selectedTask)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
